import { useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { selectAuth } from '../../../services/auth/selectors'
import { selectPrimaryLanguage } from '../../../services/user/selectors'
import { EN } from '../../../services/languages/constants'
import { useVideoConceptMomentsLazyQuery } from '../queries.generated'
import { useVideoState } from '../../OracleCommons/useVideoState'

/**
 * A single dense moment returned from the VideoConceptMoments API
 */
export interface ConceptMoment {
  startTimecode: string
  endTimecode: string
  startSeconds: number
  endSeconds: number
  conceptLabel: string
  densityScore: number
}

/**
 * Return type for the useConceptMoments hook
 */
export interface UseConceptMomentsResult {
  /** Dense moments for the current video, sorted by timecode */
  moments: ConceptMoment[] | null
  /** Whether the moments are currently being fetched */
  loading: boolean
}

/**
 * Options for the useConceptMoments hook
 */
export interface UseConceptMomentsOptions {
  /** Whether the feature is enabled (feature flag). When false, no API call is made. */
  enabled?: boolean
}

/**
 * Hook to fetch conceptually dense moments for a video.
 *
 * Fetches once per video load (contentId + featureMediaId).
 * Returns the moments array for use by useConceptTrigger (toast nudge)
 * and useConceptMomentPrompts (moment-scoped prompt pills).
 *
 * Enabled by default when the guide renders (gated by PostHog experiment).
 */
export function useConceptMoments (
  options: UseConceptMomentsOptions = {},
): UseConceptMomentsResult {
  const { enabled = true } = options

  const auth = useSelector(selectAuth)
  const jwt = auth.get('jwt')
  const language = useSelector(selectPrimaryLanguage) || EN
  const { contentId, featureMediaId } = useVideoState()
  const [fetchMoments, { data, loading }] = useVideoConceptMomentsLazyQuery()
  const hasFetched = useRef(false)
  const prevContentIdRef = useRef<string | null>(null)

  useEffect(() => {
    if (!enabled) return
    if (!contentId || !featureMediaId || !jwt) return

    // Reset fetch guard on video change
    if (contentId !== prevContentIdRef.current) {
      prevContentIdRef.current = contentId
      hasFetched.current = false
    }

    if (hasFetched.current) return

    hasFetched.current = true
    fetchMoments({
      variables: {
        input: {
          contentId,
          featureMediaId,
          language,
          maxMoments: 3,
        },
      },
      context: { jwt },
    })
  }, [enabled, contentId, featureMediaId, jwt, language, fetchMoments])

  const moments = (enabled && data?.videoConceptMoments?.moments) ?? null

  return {
    moments,
    loading,
  }
}
